import { useEffect, useMemo, useState } from "react";
import mockScenarios from "../mocks/mockScenarios";
import { cloneDefaultParameters, DEFAULT_PERIOD } from "../constants/defaults";
import { scenarioRepositoryMock } from "../services/scenarioStorage.mock";

function makeId(prefix = "scn") {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

function normalizeScenario(raw, fallbackIndex = 0) {
  const now = new Date().toISOString();
  const source = raw || {};
  return {
    id: source.id || makeId("scn"),
    name: source.name || `Cenário ${fallbackIndex + 1}`,
    kind: source.kind === "official" ? "official" : "draft",
    period: source.period || DEFAULT_PERIOD,
    parameters: {
      ...cloneDefaultParameters(),
      ...(source.parameters || {}),
    },
    notes: source.notes || "",
    createdAt: source.createdAt || now,
    updatedAt: source.updatedAt || now,
    isDirty: typeof source.isDirty === "boolean" ? source.isDirty : true,
    lastRun: source.lastRun || null,
  };
}

export default function useScenarios() {
  const [scenarios, setScenarios] = useState(() => {
    const storage = scenarioRepositoryMock.loadState();
    const stored = Array.isArray(storage?.scenarios) ? storage.scenarios : null;
    const seed = stored && stored.length ? stored : mockScenarios;
    return seed.map(normalizeScenario);
  });

  const [activeScenarioId, setActiveScenarioId] = useState(() => {
    const storage = scenarioRepositoryMock.loadState();
    const storedId = storage?.activeScenarioId || null;
    const exists = (Array.isArray(storage?.scenarios) ? storage.scenarios : mockScenarios).some(
      (s) => s?.id === storedId
    );
    if (exists) return storedId;
    return (mockScenarios[0] && mockScenarios[0].id) || null;
  });

  useEffect(() => {
    scenarioRepositoryMock.saveState({ scenarios, activeScenarioId });
  }, [scenarios, activeScenarioId]);

  useEffect(() => {
    if (!scenarios.length) return;
    if (!scenarios.some((scenario) => scenario.id === activeScenarioId)) {
      setActiveScenarioId(scenarios[0].id);
    }
  }, [scenarios, activeScenarioId]);

  const activeScenario = useMemo(
    () => scenarios.find((scenario) => scenario.id === activeScenarioId) || null,
    [scenarios, activeScenarioId]
  );

  function patchScenario(id, patcher) {
    setScenarios((prev) =>
      prev.map((scenario) => {
        if (scenario.id !== id) return scenario;
        const next = typeof patcher === "function" ? patcher(scenario) : scenario;
        return normalizeScenario(
          {
            ...scenario,
            ...next,
            updatedAt: new Date().toISOString(),
          },
          0
        );
      })
    );
  }

  function createScenario(name) {
    const now = new Date().toISOString();
    const base = activeScenario || mockScenarios[0] || {};
    const scenario = normalizeScenario(
      {
        id: makeId("scn"),
        name: (name || "").trim() || `Novo cenário ${scenarios.length + 1}`,
        kind: "draft",
        period: base.period || DEFAULT_PERIOD,
        parameters: {
          ...cloneDefaultParameters(),
          ...(base.parameters || {}),
        },
        createdAt: now,
        updatedAt: now,
        isDirty: true,
        lastRun: null,
      },
      scenarios.length
    );
    setScenarios((prev) => [scenario, ...prev]);
    setActiveScenarioId(scenario.id);
    return scenario;
  }

  function duplicateScenario(id) {
    const source = scenarios.find((scenario) => scenario.id === id) || activeScenario;
    if (!source) return null;
    const now = new Date().toISOString();
    const duplicated = normalizeScenario({
      ...source,
      id: makeId("scn"),
      name: `${source.name} (cópia)`,
      kind: "draft",
      createdAt: now,
      updatedAt: now,
      isDirty: true,
      lastRun: source.lastRun ? { ...source.lastRun } : null,
    });
    setScenarios((prev) => [duplicated, ...prev]);
    setActiveScenarioId(duplicated.id);
    return duplicated;
  }

  function renameScenario(id, nextName) {
    const name = String(nextName || "").trim();
    if (!name) return false;
    patchScenario(id, (scenario) => ({ ...scenario, name }));
    return true;
  }

  function deleteScenario(id) {
    if (scenarios.length <= 1) {
      return { ok: false, reason: "É necessário manter pelo menos 1 cenário." };
    }
    const exists = scenarios.some((scenario) => scenario.id === id);
    if (!exists) return { ok: false, reason: "Cenário não encontrado." };

    setScenarios((prev) => prev.filter((scenario) => scenario.id !== id));
    if (activeScenarioId === id) {
      const next = scenarios.find((scenario) => scenario.id !== id);
      setActiveScenarioId(next?.id || null);
    }
    return { ok: true };
  }

  function setScenarioKind(id, kind) {
    patchScenario(id, (scenario) => ({
      ...scenario,
      kind: kind === "official" ? "official" : "draft",
    }));
  }

  function updateScenarioParameters(id, patch) {
    patchScenario(id, (scenario) => ({
      ...scenario,
      parameters: {
        ...scenario.parameters,
        ...(patch || {}),
      },
      isDirty: true,
    }));
  }

  function updateScenarioPeriod(id, period) {
    patchScenario(id, (scenario) => ({
      ...scenario,
      period: period || scenario.period,
      isDirty: true,
    }));
  }

  function saveScenarioRun(id, runSnapshot) {
    patchScenario(id, (scenario) => ({
      ...scenario,
      isDirty: false,
      lastRun: runSnapshot || null,
    }));
  }

  function markAllDirty() {
    setScenarios((prev) =>
      prev.map((scenario) =>
        normalizeScenario(
          {
            ...scenario,
            isDirty: true,
            updatedAt: new Date().toISOString(),
          },
          0
        )
      )
    );
  }

  return {
    scenarios,
    activeScenarioId,
    setActiveScenarioId,
    activeScenario,
    createScenario,
    duplicateScenario,
    renameScenario,
    deleteScenario,
    setScenarioKind,
    updateScenarioParameters,
    updateScenarioPeriod,
    saveScenarioRun,
    markAllDirty,
  };
}
